import * as Sentry from '@sentry/browser';
import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import {
    createAbExperimentSelector,
    recordAssignment,
} from '@zocdoc-frontend-common/utils-ab';
import { shouldRecordExperiment } from 'src/Booking/pages/ReviewAndBook/helpers';
import reviewAndBookScanCardStateSelector from 'src/Booking/selectors/ReviewAndBookScanInsuranceCardSelector';
import {
    showStaticReviewAndBook,
    shouldShowMFAInAuthSelector,
    shouldRequireMemberIdSelector,
    shouldShowReviewAndBookScanner,
    shouldShowOutOfStateVideoVisitBanner,
} from 'src/config/ab/selector';
import {
    PCE_MENTAL_HEALTH_OON_BENEFITS,
    REQUIRE_MEMBER_ID,
} from '../../../config/ab/experiments';
import { MENTAL_HEALTH_SPECIALTIES } from '../../../constants';
import {
    PHONE_REQUIRED,
    INSURANCE_ID_REQUIRED,
    CORONA_SCREEN_SYMPTOMS,
    CORONA_SCREEN_CONTACTS,
    CORONA_SCREEN_LOCATION,
    IN_NETWORK_ONLY_REQUIREMENT,
} from '../../components/AppointmentRequirements/constants';
import {
    getRequirement,
    hasRequirement,
} from '../../components/AppointmentRequirements/helpers';
import { hasMentalHealthRequirement } from '../../components/MentalHealthQuestionnaire';
import {
    APPOINTMENT_INFO_ROOT_KEY,
    APPOINTMENT_DETAILS_ROOT_KEY,
    BookingStateProperties,
    REVIEW_AND_BOOK_ROOT_KEY,
    PHONE_ROOT_KEY,
    SCHEDULING_ROOT_KEY,
    DOCTOR_CARD_ROOT_KEY,
    ERROR_CODES,
    SURVEY_ROOT_KEY,
    USER_ROOT_KEY,
} from '../../constants';
import appointmentInfoMapStateToProps from '../../pages/AppointmentInfo/mapStateToProps';
import selectPatientMapStateToProps from '../../pages/SelectPatient/mapStateToProps';
import bookingStateSelector from '../../selectors/BookingStateSelector';
import initialBookingStateSelector from '../../selectors/InitialBookingStateSelector';
import patientSelector from '../../selectors/PatientSelector';
import { isBookingStateForCovidVaccine } from '../../utils/isCovidVaccine';
import appointmentDetailsMapStateToProps from '../AppointmentDetails/mapStateToProps';

const didReceiveSchedulingGovernmentInsuranceError = (state) => {
    const errors = get(state, [SCHEDULING_ROOT_KEY, 'errors']) || [];

    if (errors && errors.some) {
        return errors.some(
            (error) => error.code === ERROR_CODES.GOVERNMENT_INSURANCE_ERROR
        );
    } else if (errors) {
        try {
            const errorInformation = {
                msg: 'Inspect erroneous error response',
                type: typeof errors,
                keys: Object.keys(errors),
            };
            Sentry.captureException(
                new Error(JSON.stringify(errorInformation))
            );
        } catch (err) {
            Sentry.captureException(err);
        }
    }

    return false;
};

const didReceiveCoronaScreenError = (state) => {
    const errors = get(state, [SCHEDULING_ROOT_KEY, 'bookingErrors']) || [];

    if (errors && errors.length) {
        return !!find(
            errors,
            (f = {}) =>
                f.name === 'requirements.corona_screen_symptoms' ||
                f.name === 'requirements.corona_screen_contacts' ||
                f.name === 'requirements.corona_screen_location'
        );
    }

    return false;
};

const mapStateToProps = (state) => {
    checkForRootKeys(state);
    const patient = patientSelector(state);
    const professional = state[DOCTOR_CARD_ROOT_KEY].professional || {};
    const bookingState = bookingStateSelector(state);
    const { availableInsuranceCardDataId, memberId } =
        patient.selectedInsuranceCard || {};
    const requirements = get(
        bookingState,
        BookingStateProperties.requirements,
        []
    );

    const appointmentInstructions = get(
        bookingState,
        BookingStateProperties.appointmentInstructions,
        []
    );

    const carrierId = get(
        bookingState,
        BookingStateProperties.insuranceCarrierId
    );

    const insuranceIdRequired = hasRequirement(
        INSURANCE_ID_REQUIRED,
        requirements
    );

    //Member Id Required Experiment
    const recordExperiment = shouldRecordExperiment({
        requirements,
        professional,
        isMemberIdAlreadyRequired: insuranceIdRequired,
        carrierId,
    });
    if (recordExperiment) {
        recordAssignment(REQUIRE_MEMBER_ID);
    }
    const shouldRequireMemberId = shouldRequireMemberIdSelector(state);
    const insuranceIdRequiredForExperiment =
        recordExperiment && shouldRequireMemberId;

    const phoneNumberRequired = hasRequirement(PHONE_REQUIRED, requirements);

    const initialBookingState = initialBookingStateSelector(state);

    const coronaSymptomsText = get(
        getRequirement(CORONA_SCREEN_SYMPTOMS, requirements),
        'suggested_message',
        null
    );

    const coronaContactsText = get(
        getRequirement(CORONA_SCREEN_CONTACTS, requirements),
        'suggested_message',
        null
    );

    const coronaLocationText = get(
        getRequirement(CORONA_SCREEN_LOCATION, requirements),
        'suggested_message',
        null
    );

    const inNetworkOnlyBookableText = get(
        getRequirement(IN_NETWORK_ONLY_REQUIREMENT, requirements),
        'suggested_message',
        null
    );

    const insuranceData = {
        carrierId,
        planId: get(bookingState, BookingStateProperties.insurancePlanId),
        providerId: get(bookingState, BookingStateProperties.providerCloudId),
        locationCloudId: get(
            bookingState,
            BookingStateProperties.locationCloudId
        ),
    };
    let surveyQuestionsData;
    const serverSideSurveyData = state?.[SURVEY_ROOT_KEY]?.surveyQuestionsData;

    const clientSideQuestionsData =
        state?.[REVIEW_AND_BOOK_ROOT_KEY]?.surveyQuestionsData;
    // if question data was fetched locally, override server side questions
    if (clientSideQuestionsData && !isEqual(clientSideQuestionsData, {})) {
        surveyQuestionsData = clientSideQuestionsData;
    } else {
        surveyQuestionsData = serverSideSurveyData;
    }
    const isMfaEnabled =
        state?.[USER_ROOT_KEY]?.authentication_scheme === 'password_and_phone';

    const shouldFetchUser = shouldShowMFAInAuthSelector(state);
    const shouldShowInsuranceScanner = shouldShowReviewAndBookScanner(state);
    const showOutOfStateVideoVisitBanner =
        shouldShowOutOfStateVideoVisitBanner(state);

    const { isFetchingInsuranceCards } =
        reviewAndBookScanCardStateSelector(state);

    return {
        ...insuranceData,
        ...selectPatientMapStateToProps(state),
        ...appointmentInfoMapStateToProps(state),
        appointmentDetails: appointmentDetailsMapStateToProps(state),
        availableInsuranceCardDataId,
        continueWasClicked: state[REVIEW_AND_BOOK_ROOT_KEY].continueWasClicked,
        directoryId: initialBookingState.directoryId,
        hasTimeslotConstraint:
            state[APPOINTMENT_INFO_ROOT_KEY].hasTimeslotConstraint,
        insuranceIdRequired:
            insuranceIdRequired || insuranceIdRequiredForExperiment,
        insuranceCarrierName: get(
            state[APPOINTMENT_DETAILS_ROOT_KEY],
            'insuranceCarrier'
        ),
        insurancePlanName: get(
            state[APPOINTMENT_DETAILS_ROOT_KEY],
            'insurancePlan'
        ),
        isFeePerBooking: get(
            state[DOCTOR_CARD_ROOT_KEY],
            'professional.isFeePerBooking'
        ),
        isFetchingInsuranceCards,
        memberIdFromCard: memberId,
        officePhoneNumber: initialBookingState.officePhoneNumber,
        phoneNumber: get(state[PHONE_ROOT_KEY], 'phoneNumber', ''),
        phoneNumberRequired,
        professional,
        shouldShowLoadingAnimation:
            state[REVIEW_AND_BOOK_ROOT_KEY].shouldShowLoadingAnimation,
        showMissingFieldsError:
            state[REVIEW_AND_BOOK_ROOT_KEY].showMissingFieldsError,
        surveyQuestionsData: surveyQuestionsData,
        showVaccineDisclaimer: isBookingStateForCovidVaccine(state),
        updatingIsExistingPatient: get(
            state[SCHEDULING_ROOT_KEY],
            'updatingIsExistingPatient',
            false
        ),
        receivedSchedulingGovernmentInsuranceError:
            didReceiveSchedulingGovernmentInsuranceError(state),
        shouldShowMentalHealthOONBenefits:
            shouldShowMentalHealthOONBenefitsSelector(state),
        showStaticReviewAndBook: showStaticReviewAndBook(state),
        shouldShowCoronaErrorPage: didReceiveCoronaScreenError(state),
        coronaSymptomsText,
        coronaContactsText,
        coronaLocationText,
        inNetworkOnlyBookableText,
        showLifeStanceSection: hasMentalHealthRequirement(bookingState),
        isMfaEnabled: isMfaEnabled,
        shouldFetchUser: shouldFetchUser,
        shouldShowInsuranceScanner,
        appointmentInstructions,
        showOutOfStateVideoVisitBanner: showOutOfStateVideoVisitBanner,
    };
};

const checkForRootKeys = (state) => {
    if (state[APPOINTMENT_INFO_ROOT_KEY] === undefined) {
        throw new Error(
            'Expected the appointment info state to be available as ' +
                `state.${APPOINTMENT_INFO_ROOT_KEY}. ` +
                "Ensure you have added the 'appointmentInfoPageReducer' to your store's " +
                "reducers via 'combineReducers'."
        );
    }
};

const shouldShowMentalHealthOONBenefitsSelector = (state) => {
    const isAssignmentOn = isMentalHealthOONBenefitsExperimentOn(state);
    const isMentalHealthProvider = Boolean(
        state[DOCTOR_CARD_ROOT_KEY]?.professional?.specialties?.some(
            (specialty) => MENTAL_HEALTH_SPECIALTIES.has(specialty.id)
        )
    );
    // should not show moon on white label
    const directoryId = initialBookingStateSelector(state).directoryId;

    return isAssignmentOn && isMentalHealthProvider && directoryId === null;
};

const isMentalHealthOONBenefitsExperimentOn = createAbExperimentSelector(
    PCE_MENTAL_HEALTH_OON_BENEFITS
);

export default mapStateToProps;
